import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Sparkle, X, PaperPlaneRight, Lightning } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Which programs have the highest dropout-risk this semester?",
  "Summarize fee collection vs target for Q4.",
  "How many students are NEP-2020 ABC-credit eligible?",
  "Which recruiters drove placement growth this year?",
];

export default function AICopilot({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi — I'm EduOS Copilot. Ask me anything about your enrollment, fees, NEP outcomes, dropout risk, or placements." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/copilot", { message: msg, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Copilot is currently unavailable. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="copilot-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}/>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-[460px] bg-white border-l border-border flex flex-col shadow-2xl">
        <div className="eos-tracing-beam rounded-md mx-3 mt-3">
          <div className="px-4 py-3 flex items-center justify-between rounded-md bg-[hsl(244_53%_20%)] text-white">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[hsl(20_90%_55%)]"/>
              <div className="font-heading font-semibold text-sm">EduOS Copilot</div>
              <span className="text-[10px] font-mono-eos text-white/50">claude-sonnet-4-5</span>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-copilot-btn"><X size={16}/></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {messages.map((m, i) => (
            <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] rounded-md px-3.5 py-2.5 text-[13.5px] leading-relaxed ${
                m.role === "user"
                  ? "bg-[hsl(244_53%_20%)] text-white"
                  : "bg-secondary text-foreground border border-border"
              }`}>{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-secondary border border-border rounded-md px-3.5 py-2.5 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-foreground/50 animate-bounce" style={{animationDelay: "0ms"}}/>
                <span className="w-1.5 h-1.5 rounded-full bg-foreground/50 animate-bounce" style={{animationDelay: "120ms"}}/>
                <span className="w-1.5 h-1.5 rounded-full bg-foreground/50 animate-bounce" style={{animationDelay: "240ms"}}/>
              </div>
            </div>
          )}
          <div ref={bottomRef}/>
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pb-2">
            <div className="eos-overline mb-2">Try</div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-[11.5px] px-2.5 py-1.5 border border-border rounded-md hover:bg-secondary inline-flex items-center gap-1.5"
                  data-testid="copilot-suggestion"
                >
                  <Lightning size={11} weight="fill" className="text-[hsl(20_90%_48%)]"/>{s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-border p-3 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask about enrollment, fees, NEP, placements…"
            className="flex-1 px-3 py-2 text-sm bg-secondary border border-transparent focus:border-foreground/30 outline-none rounded-md"
            data-testid="copilot-input"
          />
          <Button onClick={() => send()} disabled={loading} className="h-9 px-3 rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="copilot-send-btn">
            <PaperPlaneRight size={14} weight="fill"/>
          </Button>
        </div>
      </div>
    </div>
  );
}
